//patientmanagement.reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchAllPatients,
  addPatient,
  updatePatient,
  removePatient,
  updatePatientPhoto,
} from '../actions/patientmanagement.action'
import { logout } from '../actions/auth.action'
import { Patient } from '@dataTypes/PatientInfo.types'
import { IContact } from '@dataTypes/contact.type'

type PatientState = {
  data: Patient[] | []
  loading: boolean
  hasLoadedPatients: boolean
  error: string | null
  successMessage: string | null
  selectedContact: IContact | null
  selectedPatient: Patient | null // Add this line
  selectedPatients: Patient[] | []
  refresh: number
}

const initialState: PatientState = {
  data: [],
  loading: false,
  hasLoadedPatients: false,
  error: null,
  successMessage: null,
  selectedContact: null,
  selectedPatient: null,
  selectedPatients: [],
  refresh: 0,
}

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    setSelectedPatient(
      state,
      action: PayloadAction<{ patient: Patient; contact?: IContact }>,
    ) {
      const { patient, contact } =
        action.payload ||
        ({} as {
          patient: Patient
          contact?: IContact
        })
      if (!patient || !patient.patient_id) {
        return
      }
      state.selectedPatient = patient
      state.selectedContact = contact ?? null
      const index =
        state.selectedPatients.length > 0
          ? state.selectedPatients.findIndex(
              (p) => p.patient_id === patient.patient_id,
            )
          : -1
      if (index !== -1) {
        state.selectedPatients[index] = {
          ...state.selectedPatients[index],
          ...patient,
        }
      } else {
        state.selectedPatients = [...state.selectedPatients, patient]
      }
    },
    removeSelectedPatient(state, action: PayloadAction<string>) {
      if (state.selectedPatient?.patient_id === action.payload) {
        state.selectedPatient = null
        state.selectedContact = null
      }
      state.selectedPatients = state.selectedPatients.filter(
        (patient) => patient.patient_id !== action.payload,
      )
    },
    clearSelectedPatient(state) {
      state.selectedPatient = null
    },
    clearAllSelectedPatients(state) {
      state.selectedPatient = null
      state.selectedPatients = []
    },
    restorePatientSelection(
      state,
      action: PayloadAction<{
        selectedPatient: Patient | null
        selectedPatients: Patient[]
      }>,
    ) {
      state.selectedPatient = action.payload.selectedPatient
      state.selectedPatients = action.payload.selectedPatients
    },
    refreshFromPatient(state) {
      state.refresh++
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllPatients.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllPatients.fulfilled,
        (state, action: PayloadAction<Patient[]>) => {
          state.data = action.payload
          state.loading = false
          state.hasLoadedPatients = true
          state.successMessage = 'Patients loaded successfully'
        },
      )
      .addCase(fetchAllPatients.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(addPatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        addPatient.fulfilled,
        (state, action: PayloadAction<Patient>) => {
          state.data = [...state.data, action.payload]
          state.loading = false
          state.successMessage = 'Patient added successfully'
        },
      )
      .addCase(addPatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(updatePatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updatePatient.fulfilled,
        (state, action: PayloadAction<Patient>) => {
          const index = state.data.findIndex(
            (patient) => patient.patient_id === action.payload.patient_id,
          )
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
            }
          }
          if (state.selectedPatient?.patient_id === action.payload.patient_id) {
            state.selectedPatient = {
              ...state.selectedPatient,
              ...action.payload,
            }
          }
          const indexPatient = state.selectedPatients.findIndex(
            (patient) => patient.patient_id === action.payload.patient_id,
          )
          if (indexPatient !== -1) {
            state.selectedPatients[indexPatient] = action.payload
          }

          state.loading = false
          state.successMessage = 'Patient updated successfully'
        },
      )
      .addCase(updatePatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(updatePatientPhoto.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updatePatientPhoto.fulfilled,
        (state, action: PayloadAction<Patient>) => {
          const index = state.data.findIndex(
            (patient) => patient.patient_id === action.payload.patient_id,
          )
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
            }
          }
          if (state.selectedPatient?.patient_id === action.payload.patient_id) {
            state.selectedPatient = {
              ...state.selectedPatient,
              ...action.payload,
            }
          }
          const indexPatient = state.selectedPatients.findIndex(
            (patient) => patient.patient_id === action.payload.patient_id,
          )
          if (indexPatient !== -1) {
            state.selectedPatients[indexPatient] = action.payload
          }

          state.loading = false
          state.successMessage = 'Patient updated successfully'
        },
      )
      .addCase(updatePatientPhoto.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(removePatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        removePatient.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.data = state.data.filter(
            (patient) => patient.patient_id !== action.payload,
          )
          state.selectedPatients = state.selectedPatients.filter(
            (patient) => patient.patient_id !== action.payload,
          )
          state.selectedContact = null
          if (state.selectedPatient?.patient_id === action.payload) {
            state.selectedPatient = null
          }
          state.loading = false
          state.successMessage = 'Patient removed successfully'
        },
      )
      .addCase(removePatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(logout.fulfilled, (state) => {
        state.selectedPatient = null
        state.selectedContact = null
        state.selectedPatients = []
        state.data = []
        state.loading = false
        state.hasLoadedPatients = false
        state.error = null
        state.successMessage = null
        state.refresh = 0
      })
      .addMatcher(
        (action) =>
          action.type.startsWith('patients/') &&
          action.type.endsWith('/pending'),
        (state) => {
          state.loading = true
          state.error = null
          state.successMessage = null
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('patients/') &&
          action.type.endsWith('/rejected'),
        (state, action) => {
          state.loading = false
          state.error = (action as any).error?.message || 'Action failed'
          state.successMessage = null
        },
      )
  },
})

export const {
  setSelectedPatient,
  clearSelectedPatient,
  clearAllSelectedPatients,
  removeSelectedPatient,
  restorePatientSelection,
  refreshFromPatient,
} = patientSlice.actions
export default patientSlice.reducer
